/******
 * This file was generated by a Rake task.
 * Do not modify this file manually.
 * Add changes to the `Team.ts` file.
 ******/
// oxlint-disable-next-line no-unused-vars
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
dayjs.extend(timezone);

import { Team } from "./Team";

import { Audit } from "./Audit";
import { CalendarEventResource } from "./CalendarEventResource";
import { ExecutiveCommandView } from "./ExecutiveCommandView";
import { Material } from "./Material";
import { Message } from "./Message";
import { NotificationList } from "./NotificationList";
import { Permission } from "./Permission";
import { Project } from "./Project";
import { SsoEmailDomain } from "./SsoEmailDomain";
import { UsersMostRecentProject } from "./UsersMostRecentProject";
import { VendorLink } from "./VendorLink";
import { WorkosTeamMapping } from "./WorkosTeamMapping";

export type ssoProvider = "unset" | "workos";

export const ssoProviderValues = { unset: 0, workos: 3 };

export abstract class TeamModel {
    id: number;
    accountToken: string | null;
    address1: string | null;
    address2: string | null;
    brandColors: string | null;
    city: string | null;
    createdAt: Dayjs;
    gcCanManageCompanyInfo: boolean;
    initials: string | null;
    lat: number | null;
    lon: number | null;
    phoneNumber: string | null;
    ssoEnforced: boolean;
    ssoProvider: ssoProvider;
    ssoProviderData: any;
    state: string | null;
    subdomain: string | null;
    teamName: string | null;
    updatedAt: Dayjs;
    websiteUrl: string | null;
    zipCode: string | null;
    avatarUrl: string | null;
    audits: Audit[] | undefined;
    calendarEventResources: CalendarEventResource[] | undefined;
    executiveCommandViews: ExecutiveCommandView[] | undefined;
    materials: Material[] | undefined;
    messages: Message[] | undefined;
    notificationLists: NotificationList[] | undefined;
    ownedProjects: Project[] | undefined;
    permissions: Permission[] | undefined;
    ssoEmailDomains: SsoEmailDomain[] | undefined;
    usersMostRecentProjects: UsersMostRecentProject[] | undefined;
    vendorLinks: VendorLink[] | undefined;
    workosTeamMapping: WorkosTeamMapping | null | undefined;
    _destroy: boolean | undefined;

    /* eslint-disable max-statements */
    constructor(
        id: number,
        accountToken: string | null,
        address1: string | null,
        address2: string | null,
        brandColors: string | null,
        city: string | null,
        createdAt: Dayjs,
        gcCanManageCompanyInfo: boolean,
        initials: string | null,
        lat: number | null,
        lon: number | null,
        phoneNumber: string | null,
        ssoEnforced: boolean,
        ssoProvider: ssoProvider,
        ssoProviderData: any,
        state: string | null,
        subdomain: string | null,
        teamName: string | null,
        updatedAt: Dayjs,
        websiteUrl: string | null,
        zipCode: string | null,
        avatarUrl: string | null,
        audits: Audit[] | undefined,
        calendarEventResources: CalendarEventResource[] | undefined,
        executiveCommandViews: ExecutiveCommandView[] | undefined,
        materials: Material[] | undefined,
        messages: Message[] | undefined,
        notificationLists: NotificationList[] | undefined,
        ownedProjects: Project[] | undefined,
        permissions: Permission[] | undefined,
        ssoEmailDomains: SsoEmailDomain[] | undefined,
        usersMostRecentProjects: UsersMostRecentProject[] | undefined,
        vendorLinks: VendorLink[] | undefined,
        workosTeamMapping: WorkosTeamMapping | null | undefined
    ) {
        this.id = id;
        this.accountToken = accountToken;
        this.address1 = address1;
        this.address2 = address2;
        this.brandColors = brandColors;
        this.city = city;
        this.createdAt = createdAt;
        this.gcCanManageCompanyInfo = gcCanManageCompanyInfo;
        this.initials = initials;
        this.lat = lat;
        this.lon = lon;
        this.phoneNumber = phoneNumber;
        this.ssoEnforced = ssoEnforced;
        this.ssoProvider = ssoProvider;
        this.ssoProviderData = ssoProviderData;
        this.state = state;
        this.subdomain = subdomain;
        this.teamName = teamName;
        this.updatedAt = updatedAt;
        this.websiteUrl = websiteUrl;
        this.zipCode = zipCode;
        this.avatarUrl = avatarUrl;
        this.audits = audits;
        this.calendarEventResources = calendarEventResources;
        this.executiveCommandViews = executiveCommandViews;
        this.materials = materials;
        this.messages = messages;
        this.notificationLists = notificationLists;
        this.ownedProjects = ownedProjects;
        this.permissions = permissions;
        this.ssoEmailDomains = ssoEmailDomains;
        this.usersMostRecentProjects = usersMostRecentProjects;
        this.vendorLinks = vendorLinks;
        this.workosTeamMapping = workosTeamMapping;
    }
    /* eslint-enable max-statements */

    // oxlint-disable-next-line complexity
    static fromApiObject(json: any, timezone: string): Team {
        const newObject = new Team(
            json["id"],
            json["account_token"],
            json["address_1"],
            json["address_2"],
            json["brand_colors"],
            json["city"],
            dayjs(json["created_at"]).tz(timezone),
            json["gc_can_manage_company_info"],
            json["initials"],
            json["lat"],
            json["lon"],
            json["phone_number"],
            json["sso_enforced"],
            json["sso_provider"],
            json["sso_provider_data"],
            json["state"],
            json["subdomain"],
            json["team_name"],
            dayjs(json["updated_at"]).tz(timezone),
            json["website_url"],
            json["zip_code"],
            json["avatar_url"],
            json["audits"]?.map((item: any) => Audit.fromApiObject(item, timezone)),
            json["calendar_event_resources"]?.map((item: any) => CalendarEventResource.fromApiObject(item, timezone)),
            json["executive_command_views"]?.map((item: any) => ExecutiveCommandView.fromApiObject(item, timezone)),
            json["materials"]?.map((item: any) => Material.fromApiObject(item, timezone)),
            json["messages"]?.map((item: any) => Message.fromApiObject(item, timezone)),
            json["notification_lists"]?.map((item: any) => NotificationList.fromApiObject(item, timezone)),
            json["owned_projects"]?.map((item: any) => Project.fromApiObject(item, timezone)),
            json["permissions"]?.map((item: any) => Permission.fromApiObject(item, timezone)),
            json["sso_email_domains"]?.map((item: any) => SsoEmailDomain.fromApiObject(item, timezone)),
            json["users_most_recent_projects"]?.map((item: any) =>
                UsersMostRecentProject.fromApiObject(item, timezone)
            ),
            json["vendor_links"]?.map((item: any) => VendorLink.fromApiObject(item, timezone)),
            json["workos_team_mapping"] == null
                ? json["workos_team_mapping"]
                : WorkosTeamMapping.fromApiObject(json["workos_team_mapping"], timezone)
        );

        return newObject;
    }

    // oxlint-disable-next-line complexity
    toApiObject(): any {
        return {
            id: this.id,
            account_token: this.accountToken,
            address_1: this.address1,
            address_2: this.address2,
            brand_colors: this.brandColors,
            city: this.city,
            created_at: this.createdAt,
            gc_can_manage_company_info: this.gcCanManageCompanyInfo,
            initials: this.initials,
            lat: this.lat,
            lon: this.lon,
            phone_number: this.phoneNumber,
            sso_enforced: this.ssoEnforced,
            sso_provider: this.ssoProvider,
            sso_provider_data: this.ssoProviderData,
            state: this.state,
            subdomain: this.subdomain,
            team_name: this.teamName,
            updated_at: this.updatedAt,
            website_url: this.websiteUrl,
            zip_code: this.zipCode,
            audits: this.audits?.map((item) => item.toApiObject()),
            calendar_event_resources_attributes: this.calendarEventResources?.map((item) => item.toApiObject()),
            executive_command_views: this.executiveCommandViews?.map((item) => item.toApiObject()),
            materials: this.materials?.map((item) => item.toApiObject()),
            messages: this.messages?.map((item) => item.toApiObject()),
            notification_lists: this.notificationLists?.map((item) => item.toApiObject()),
            owned_projects: this.ownedProjects?.map((item) => item.toApiObject()),
            permissions: this.permissions?.map((item) => item.toApiObject()),
            sso_email_domains: this.ssoEmailDomains?.map((item) => item.toApiObject()),
            users_most_recent_projects: this.usersMostRecentProjects?.map((item) => item.toApiObject()),
            vendor_links: this.vendorLinks?.map((item) => item.toApiObject()),
            _destroy: this._destroy,
        };
    }

    // NOTE: This could potentially cause infinite loops if the object graph is
    // circular.
    inAlternateTimezone(timezone: string): Team {
        const clone = Object.create(this) as Team;

        for (const key of Object.keys(this)) {
            if (this[key] instanceof dayjs) {
                // @ts-expect-error: Typescript thinks that this is not a dayjs object even though it is checked on the line before
                clone[key] = this[key].tz(timezone);
            } else if (Array.isArray(this[key])) {
                clone[key] = this[key].map((item) =>
                    item.inAlternateTimezone ? item.inAlternateTimezone(timezone) : item
                );
            } else if (this[key] && this[key].inAlternateTimezone) {
                clone[key] = this[key].inAlternateTimezone(timezone);
            }
        }

        return clone;
    }
}
